import { OpenInNew } from "@mui/icons-material";
import { Box, Button, Paper, Stack, Tooltip, Typography } from "@mui/material";
import type { ReactNode } from "react";

type InfoCardStatus = "completed" | "pending" | "ready" | "error" | "warning";

type InfoCardProps = {
	icon: ReactNode;
	title: string;
	subtitle?: string;
	status?: InfoCardStatus;
	statusTooltip?: string;
	actionLabel?: string;
	actionHref?: string;
	onAction?: () => void;
	customActionButton?: ReactNode;
};

const statusColors: Record<InfoCardStatus, string> = {
	completed: "#22C55E",
	ready: "#22C55E",
	pending: "#F59E0B",
	warning: "#F59E0B",
	error: "#EF4444",
};

export const InfoCard = ({
	icon,
	title,
	subtitle,
	status,
	statusTooltip,
	actionLabel,
	actionHref,
	onAction,
	customActionButton,
}: InfoCardProps) => {
	const statusIndicator = status && (
		<Tooltip title={statusTooltip || ""} arrow placement="top">
			<Box
				sx={{
					width: 10,
					height: 10,
					borderRadius: "50%",
					backgroundColor: statusColors[status],
					flexShrink: 0,
				}}
			/>
		</Tooltip>
	);

	const actionButton = actionLabel && (actionHref || onAction) && (
		<Button
			variant="text"
			size="small"
			color="primary"
			onClick={onAction}
			{...(actionHref && {
				component: "a",
				href: actionHref,
				target: "_blank",
				rel: "noopener noreferrer",
			})}
			endIcon={actionHref ? <OpenInNew /> : undefined}
			sx={{
				textTransform: "none",
				fontWeight: 500,
				minWidth: "auto",
				px: 1,
			}}
		>
			{actionLabel}
		</Button>
	);

	return (
		<Paper
			elevation={0}
			sx={{
				p: 2.5,
				border: "1px solid #E9EAEB",
				borderRadius: 2,
				height: "100%",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Stack spacing={2} sx={{ height: "100%" }}>
				<Stack
					direction="row"
					spacing={2}
					alignItems="flex-start"
					sx={{ flex: 1 }}
				>
					<Box
						sx={{
							color: "primary.main",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							p: 1,
							borderRadius: 1,
							backgroundColor: "rgba(220, 38, 38, 0.08)",
						}}
					>
						{icon}
					</Box>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Stack direction="row" alignItems="center" spacing={1}>
							<Typography
								variant="subtitle1"
								fontWeight={600}
								sx={{
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}
							>
								{title}
							</Typography>
							{statusIndicator}
						</Stack>
						{subtitle && (
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ mt: 0.5 }}
							>
								{subtitle}
							</Typography>
						)}
					</Box>
				</Stack>
				{(actionButton || customActionButton) && (
					<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
						{customActionButton || actionButton}
					</Box>
				)}
			</Stack>
		</Paper>
	);
};
